import { useEffect, useMemo, useRef, useState } from "react";
import { Bubble, XProvider } from "@ant-design/x";
import { sanitizeBubbleListItems } from "@/hooks/opsmate/bubbleItems";
import { useMessageRoles } from "@/hooks/opsmate/useMessageRoles";

interface ConversationMessageListProps {
  items: any[];
  targetQuestionId?: string;
  targetAnswerId?: string;
  height?: string | number;
  /** 审计/只读场景下隐藏「翻译 / 点赞 / 点踩」按钮（仅保留复制） */
  hideTranslateAndFeedback?: boolean;
}

export default function ConversationMessageList({
  items,
  targetQuestionId,
  targetAnswerId,
  height = "100%",
  hideTranslateAndFeedback = false,
}: ConversationMessageListProps) {
  const listRef = useRef<any>(null);
  const itemsRef = useRef<any[]>([]);
  const [renderItems, setRenderItems] = useState<any[]>(items);
  const [, setDocsCollapsed] = useState(true);
  const [, setHighlightedDocGuid] = useState("");
  const [, setAIReasoningVisible] = useState(false);
  const [, setAIReasoning] = useState("");
  const [, setDislikeConfirmTarget] = useState<any>(null);
  const [, setDislikeConfirmVisible] = useState(false);

  const highlightedItems = useMemo(() => {
    const targetKeys = new Set(
      [targetQuestionId ? `${targetQuestionId}-q` : "", targetAnswerId ? `${targetAnswerId}-a` : ""].filter(
        Boolean,
      ),
    );
    return items.map((item) =>
      targetKeys.has(String(item.key))
        ? {
            ...item,
            className: [item.className, "opsmate-audit-target"].filter(Boolean).join(" "),
            style: {
              ...(item.style ?? {}),
              borderRadius: 12,
              boxShadow: "0 0 0 2px rgba(131, 0, 81, 0.16)",
            },
          }
        : item,
    );
  }, [items, targetAnswerId, targetQuestionId]);

  useEffect(() => {
    setRenderItems(highlightedItems);
  }, [highlightedItems]);

  itemsRef.current = renderItems;

  const { roles } = useMessageRoles({
    itemsRef,
    setDocsCollapsed,
    setHighlightedDocGuid,
    setAIReasoningVisible,
    setAIReasoning,
    handleTranslate: async () => undefined,
    handleFeedbackChange: async () => undefined,
    regenerateMessage: async () => undefined,
    setDislikeConfirmTarget,
    setDislikeConfirmVisible,
    hideTranslateAndFeedback,
  });

  const roleConfig = useMemo(() => roles(setRenderItems), [roles]);
  const bubbleItems = useMemo(() => sanitizeBubbleListItems(renderItems), [renderItems]);

  useEffect(() => {
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(".opsmate-audit-target");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        listRef.current?.scrollTo?.({ top: "bottom", behavior: "smooth" });
      }
    });
  }, [bubbleItems.length, targetAnswerId, targetQuestionId]);

  return (
    <XProvider theme={{ token: { colorPrimary: "#830050", borderRadiusLG: 4 } }}>
      <div style={{ height, width: "100%" }}>
        <Bubble.List
          autoScroll={false}
          items={bubbleItems}
          style={{ height: "100%", overflowY: "auto" }}
          role={roleConfig}
          ref={listRef}
        />
      </div>
    </XProvider>
  );
}
